import './ShortlistedCandidates.scss';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Checkbox, Typography } from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFetchList } from 'services/common/react-query-common-service';
import { useAppSelector } from 'store/hooks';
import { SALES_ENDPOINT } from 'utils/Constant';

import StylishLoader from '../../components/StylishLoader';
import { TeamUserRoles } from '../../enum/TeamUserRoles';

const NoMatches = () => (
  <Box className="no-candidates">
    <Box className="icon-box">
      <svg
        width="36"
        height="36"
        viewBox="0 0 36 36"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M14 3C7.92487 3 3 7.92487 3 14C3 20.0751 7.92487 25 14 25C20.0751 25 25 20.0751 25 14C25 7.92487 20.0751 3 14 3ZM0 14C0 6.26801 6.26801 0 14 0C21.732 0 28 6.26801 28 14C28 17.3257 26.8404 20.3805 24.9035 22.7822L35.5607 33.4393C36.1464 34.0251 36.1464 34.9749 35.5607 35.5607C34.9749 36.1464 34.0251 36.1464 33.4393 35.5607L22.7822 24.9035C20.3805 26.8404 17.3257 28 14 28C6.26801 28 0 21.732 0 14ZM14 12C14.8284 12 15.5 12.6716 15.5 13.5V20.5C15.5 21.3284 14.8284 22 14 22C13.1716 22 12.5 21.3284 12.5 20.5V13.5C12.5 12.6716 13.1716 12 14 12ZM14 10C15.1046 10 16 9.10457 16 8C16 6.89543 15.1046 6 14 6C12.8954 6 12 6.89543 12 8C12 9.10457 12.8954 10 14 10Z"
          fill="currentColor"
        />
      </svg>
    </Box>
    <Typography className="title">No candidates shortlisted yet</Typography>
    <Typography className="subtitle">
      Review the Matches tab and Shortlist the best profiles to move them here
      for final review.
    </Typography>
  </Box>
);

interface ShortlistedCandidatesProps {
  jobId?: string;
  selectedIds: Set<string>;
  onToggleSelection: (id: string, item: any) => void;
  tableType?: string;
  score?: string;
  onOpenCandidateModal?: (candidateId: string) => void;
  data?: any;
  isLoading?: boolean;
}

const ShortlistedCandidates: React.FC<ShortlistedCandidatesProps> = ({
  jobId,
  selectedIds,
  onToggleSelection,
  score,
  tableType,
  onOpenCandidateModal,
  data,
  isLoading,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo: userDetail }: any = useAppSelector(
    (store: any) => store.userLogin,
  );

  const navigateTo = (item: any, jobId: string) => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const basePath =
      pathSegments.length > 0 ? `/${pathSegments[0]}` : '/business-head';
    const candidateId = item?.id || '';

    navigate(`${basePath}/view-candidate/${String(candidateId)}`, {
      state: {
        Params: {
          candidateInfo: item,
          jobId: jobId,
          score,
          tableType,
        },
      },
    });
  };
  const { data: internalData, isLoading: internalLoading } = useFetchList<any>(
    ['shortlisted-candidates', jobId, score],
    `${SALES_ENDPOINT.MatchedCandidates}/${jobId}?score=${score}&type=shortlisted&sourceQueue=jobQueue`,
    {
      enabled: !!jobId && !data,
      refetchOnMount: 'always',
    },
  );

  const candidatesData = data || internalData;
  const loading = isLoading !== undefined ? isLoading : internalLoading;

  if (loading) {
    return (
      <Box className="loader-container">
        <StylishLoader mode="table" text="Loading shortlisted candidates..." />
      </Box>
    );
  }

  const candidates = candidatesData?.data || [];

  if (candidates.length === 0) {
    return (
      <Box className="shortlisted-candidates-container">
        <NoMatches />
      </Box>
    );
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Box className="shortlisted-candidates-container">
      {candidates.map((item: any) => {
        const c = item.candidate;
        const firstName = String(c?.firstName || '');
        const middleName = String(c?.middleName || '');
        const lastName = String(c?.lastName || '');
        const name = `${firstName} ${middleName} ${lastName}`.trim() || '-';

        const matchPercentage = item?.matchPercentage || 0;
        const match = `${String(matchPercentage)}%`;
        const visa = c?.workStatus?.name || '';
        const source = c?.source || '';

        const availability = c?.benchStatus || '';
        const availabilityDate = c?.benchAvailability || ''; // Mocked as per image if missing

        const location =
          [c?.city, c?.state, c?.country].filter(Boolean).join(', ') || '';
        const phone = c?.phone || c?.mobileNumber || '';
        const email = c?.email || '';

        const contactLine = [email, phone, location].filter(Boolean);

        const submittedBy = item?.shortlistedUser
          ? `${String(item.shortlistedUser.firstName)} ${String(
              item.shortlistedUser.lastName,
            )}`
          : '-';
        const submittedOn =
          formatDate(item?.shortlistedAt || item?.createdAt) !== 'N/A'
            ? formatDate(item?.shortlistedAt || item?.createdAt)
            : '';

        const cardId = String(item.id);

        return (
          <Box
            key={cardId}
            className="candidate-card"
            onClick={() =>
              userDetail?.teamUserRole === TeamUserRoles.RECRUITER ||
              userDetail?.teamUserRole === TeamUserRoles.TEAM_LEADER
                ? onToggleSelection(cardId, item)
                : {}
            }
            sx={{ cursor: 'pointer' }}>
            <Box className="card-content">
              <Box className="top-row">
                <Box className="badges-group">
                  {match && (
                    <Box
                      className="badge match-score"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onOpenCandidateModal) {
                          onOpenCandidateModal(String(item.id));
                        }
                      }}
                      sx={{
                        '&:hover': onOpenCandidateModal ? { opacity: 0.8 } : {},
                        cursor: onOpenCandidateModal ? 'pointer' : 'default',
                      }}>
                      {match}
                    </Box>
                  )}
                  {visa && <Box className="badge work-status">{visa}</Box>}
                  {source && <Box className="badge source-badge">{source}</Box>}
                </Box>
              </Box>

              <Box className="info-body">
                {(userDetail?.teamUserRole === TeamUserRoles.RECRUITER ||
                  userDetail?.teamUserRole === TeamUserRoles.TEAM_LEADER) && (
                  <Box className="checkbox-section">
                    <Checkbox
                      checked={selectedIds.has(cardId)}
                      disableRipple
                      sx={{ pointerEvents: 'none' }}
                    />
                  </Box>
                )}

                <Box className="details-wrapper">
                  <Box className="main-info-row">
                    <Box className="name-section">
                      <span
                        className="name"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateTo(item, jobId || '');
                        }}
                        style={{ cursor: 'pointer' }}>
                        {name}
                      </span>
                      {availability && (
                        <span className="availability">
                          <CheckCircleIcon className="check-icon" />
                          {availability}
                          <span className="dot">•</span>
                          {availabilityDate}
                        </span>
                      )}
                    </Box>

                    <Box className="submission-info">
                      <Box className="info-block">
                        <Typography className="label">Submitted by</Typography>
                        <Typography className="value">{submittedBy}</Typography>
                      </Box>
                      <Box className="info-block">
                        <Typography className="label">Submitted on</Typography>
                        <Typography className="value">{submittedOn}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box className="contact-row">
                    {contactLine.map((text, idx) => (
                      <React.Fragment key={idx}>
                        <span>{text}</span>
                        {idx < contactLine.length - 1 && (
                          <span className="contact-dot">•</span>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ShortlistedCandidates;
